/*global google*/

// Utilities for looking at zoom vs bounds
type size = { width: number; height: number };

export function getZoomLevelForBounds(bounds: google.maps.LatLngBounds, mapDim: size) {
    var WORLD_DIM = { height: 256, width: 256 };
    var ZOOM_MAX = 21;

    function latRad(lat: number) {
        var sin = Math.sin(lat * Math.PI / 180);
        var radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
        return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
    }

    function zoom(mapPx: number, worldPx: number, fraction: number) {
        return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
    }

    var ne = bounds.getNorthEast();
    var sw = bounds.getSouthWest();

    var latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

    var lngDiff = ne.lng() - sw.lng();
    var lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;

    var latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
    var lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
}

export function getBoundsForZoomLevel(map: google.maps.Map, zoom: number) {
    var projection = map.getProjection();
    var center = map.getCenter();
    var div = map.getDiv();
    var zoomFactor = Math.pow(2, zoom) * 2;
    var dw = div.clientWidth / zoomFactor;
    var dh = div.clientHeight / zoomFactor;
    var cpx = projection.fromLatLngToPoint(center);

    return new google.maps.LatLngBounds(
        projection.fromPointToLatLng(new google.maps.Point(cpx.x - dw, cpx.y + dh)),
        projection.fromPointToLatLng(new google.maps.Point(cpx.x + dw, cpx.y - dh))
    );
}

export function getPixelBounds(map: google.maps.Map, bounds: google.maps.LatLngBounds): { x: number, y: number, width: number, height: number }
export function getPixelBounds(map: google.maps.Map, southWest: google.maps.LatLng, northEast: google.maps.LatLng): { x: number, y: number, width: number, height: number }

export function getPixelBounds(map: google.maps.Map, arg1: google.maps.LatLng | google.maps.LatLngBounds, northEast?: google.maps.LatLng) {
    var southWest: google.maps.LatLng;
    if (arg1 instanceof google.maps.LatLngBounds) {
        southWest = arg1.getSouthWest();
        northEast = arg1.getNorthEast();
    } else {
        southWest = arg1;
        northEast = northEast!;
    }

    var projection = map.getProjection();
    var zoomFactor = Math.pow(2, map.getZoom());

    var llpx = projection.fromLatLngToPoint(southWest);
    var urpx = projection.fromLatLngToPoint(northEast);

    return { x: llpx.x * zoomFactor, y: urpx.y * zoomFactor, width: (urpx.x - llpx.x) * zoomFactor, height: (llpx.y - urpx.y) * zoomFactor }
}

// Enums that match google maps enums, but are declare here so we can use them before loading the Maps API script

export enum ControlPosition {
    /** Elements are positioned in the center of the bottom row. */
    BOTTOM_CENTER,
    /**
     * Elements are positioned in the bottom left and flow towards the middle.
     * Elements are positioned to the right of the Google logo.
     */
    BOTTOM_LEFT,
    /**
     * Elements are positioned in the bottom right and flow towards the middle.
     * Elements are positioned to the left of the copyrights.
     */
    BOTTOM_RIGHT,
    /**
     * Elements are positioned on the left, above bottom-left elements, and flow
     * upwards.
     */
    LEFT_BOTTOM,
    /** Elements are positioned in the center of the left side. */
    LEFT_CENTER,
    /**
     * Elements are positioned on the left, below top-left elements, and flow
     * downwards.
     */
    LEFT_TOP,
    /**
     * Elements are positioned on the right, above bottom-right elements, and
     * flow upwards.
     */
    RIGHT_BOTTOM,
    /** Elements are positioned in the center of the right side. */
    RIGHT_CENTER,
    /**
       Elements are positioned on the right, below top-right elements, and flow
       downwards.
     */
    RIGHT_TOP,
    /**    Elements are positioned in the center of the top row. */
    TOP_CENTER,
    /** Elements are positioned in the top left and flow towards the middle. */
    TOP_LEFT,
    /** Elements are positioned in the top right and flow towards the middle. */
    TOP_RIGHT
}

export function getControlPosition(position: ControlPosition): google.maps.ControlPosition;
export function getControlPosition(position?: ControlPosition): google.maps.ControlPosition | undefined;

export function getControlPosition(position?: ControlPosition) {
    return position === undefined ? undefined : google.maps.ControlPosition[ControlPosition[position] as keyof typeof google.maps.ControlPosition];
}

export enum ZoomControlStyle {
    DEFAULT, LARGE, SMALL
}

export const getZoomControlStyle = (style?: ZoomControlStyle) => style === undefined ? undefined : google.maps.ZoomControlStyle[ZoomControlStyle[style] as keyof typeof google.maps.ZoomControlStyle];


export enum Animation { BOUNCE, DROP, NONE }
export const getAnimation = (animation?: Animation) => (animation === undefined || animation === Animation.NONE) ? undefined : google.maps.Animation[Animation[animation] as keyof typeof google.maps.Animation];


